import React from 'react';

type Locale = 'zh' | 'en';

const dictionaries = {
  zh: {
    localeLabel: '简体中文',
    toggleLocaleLabel: 'Switch to English',
    tagline: '高质量 · 低延迟 · 企业级安全 · 全球互联',
    entryTitle: '会议入口',
    entryMeta: '输入会议码，或打开后台生成的会议链接。',
    meetingCode: '会议码',
    meetingCodePlaceholder: '请输入会议码',
    invalidMeetingCode: '请输入有效会议码',
    enterMeeting: '进入 Meeting',
    welcome: '欢迎',
    loadingMeeting: '正在加载会议...',
    unavailableMeeting: '会议暂不可用',
    meetingNotFound: '会议不存在',
    joinTitle: '欢迎加入会议',
    joinMeta: '请输入您的用户名以加入会议',
    name: '姓名',
    namePlaceholder: '请输入用户名',
    password: '会议密码',
    passwordPlaceholder: '请输入会议密码',
    nameRequired: '请输入姓名',
    incorrectPassword: '会议密码不正确',
    camera: '摄像头',
    microphone: '麦克风',
    joining: '正在加入...',
    joinMeeting: '加入会议',
    featuresLabel: '会议特性',
    secureFeatureTitle: '安全加密',
    secureFeatureText: '端到端加密保护',
    lowLatencyFeatureTitle: '低延迟',
    lowLatencyFeatureText: '全球实时通信',
    qualityFeatureTitle: '高质量',
    qualityFeatureText: 'HD 音视频体验',
    e2eeUnsupported: '当前浏览器不支持加密会议，请更新浏览器后重试。',
    mediaDeviceError: '没有找到可用的麦克风或摄像头，已继续加入会议。',
    meetingErrorPrefix: '会议连接异常',
    encryptionErrorPrefix: '会议加密异常',
    noticePrefix: '加入会议即表示您同意我们的',
    privacyPolicy: '隐私政策',
    noticeJoiner: '和',
    terms: '服务条款',
    mediaDevices: '媒体设备',
    recording: '录制',
    speaker: '扬声器和耳机',
    speakerControl: '扬声器',
    audioOutput: '播放设备',
    recordMeeting: '录制会议',
    recordingActive: '会议正在录制',
    recordingInactive: '当前会议没有录制',
    startRecording: '开始录制',
    stopRecording: '停止录制',
    close: '关闭',
    disableNoiseCancellation: '关闭增强降噪',
    enableNoiseCancellation: '开启增强降噪',
    shareScreen: '共享屏幕',
    stopScreenShare: '停止共享',
    chat: '聊天',
    settings: '设置',
    leave: '离开',
    messages: '消息',
    messagePlaceholder: '输入消息...',
    send: '发送',
    startMedia: '允许播放媒体',
    backgroundEffects: '背景效果',
    noBackground: '无',
    blurBackground: '模糊',
    deskBackground: '书桌',
    natureBackground: '自然',
  },
  en: {
    localeLabel: 'English',
    toggleLocaleLabel: '切换到简体中文',
    tagline: 'HD, secure, real-time video meetings',
    entryTitle: 'Meeting Entry',
    entryMeta: 'Enter a meeting code, or open a meeting link created by the admin.',
    meetingCode: 'Meeting code',
    meetingCodePlaceholder: 'Enter meeting code',
    invalidMeetingCode: 'Enter a valid meeting code',
    enterMeeting: 'Enter Meeting',
    welcome: 'Welcome',
    loadingMeeting: 'Loading meeting...',
    unavailableMeeting: 'Meeting is unavailable',
    meetingNotFound: 'Meeting not found',
    joinTitle: 'Welcome to the meeting',
    joinMeta: 'Enter your username to join the meeting',
    name: 'Name',
    namePlaceholder: 'Enter username',
    password: 'Meeting password',
    passwordPlaceholder: 'Enter meeting password',
    nameRequired: 'Enter your name',
    incorrectPassword: 'Incorrect meeting password',
    camera: 'Camera',
    microphone: 'Microphone',
    joining: 'Joining...',
    joinMeeting: 'Join meeting',
    featuresLabel: 'Meeting features',
    secureFeatureTitle: 'Encrypted',
    secureFeatureText: 'End-to-end protection',
    lowLatencyFeatureTitle: 'Low latency',
    lowLatencyFeatureText: 'Real-time global calls',
    qualityFeatureTitle: 'High quality',
    qualityFeatureText: 'HD audio and video',
    e2eeUnsupported:
      'This browser does not support encrypted meetings. Update your browser and try again.',
    mediaDeviceError: 'No available microphone or camera was found. You have joined the meeting.',
    meetingErrorPrefix: 'Meeting connection error',
    encryptionErrorPrefix: 'Meeting encryption error',
    noticePrefix: 'By joining, you agree to our',
    privacyPolicy: 'Privacy Policy',
    noticeJoiner: 'and',
    terms: 'Terms of Service',
    mediaDevices: 'Media Devices',
    recording: 'Recording',
    speaker: 'Speaker & Headphones',
    speakerControl: 'Speaker',
    audioOutput: 'Audio Output',
    recordMeeting: 'Record Meeting',
    recordingActive: 'Meeting is currently being recorded',
    recordingInactive: 'No active recordings for this meeting',
    startRecording: 'Start Recording',
    stopRecording: 'Stop Recording',
    close: 'Close',
    disableNoiseCancellation: 'Disable Enhanced Noise Cancellation',
    enableNoiseCancellation: 'Enable Enhanced Noise Cancellation',
    shareScreen: 'Share screen',
    stopScreenShare: 'Stop screen share',
    chat: 'Chat',
    settings: 'Settings',
    leave: 'Leave',
    messages: 'Messages',
    messagePlaceholder: 'Enter a message...',
    send: 'Send',
    startMedia: 'Click to allow media playback',
    backgroundEffects: 'Background Effects',
    noBackground: 'None',
    blurBackground: 'Blur',
    deskBackground: 'Desk',
    natureBackground: 'Nature',
  },
} as const;

function applyDocumentLocale(locale: Locale) {
  document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
}

function detectLocale(): Locale {
  if (typeof navigator === 'undefined') {
    return 'zh';
  }
  return navigator.languages?.some((language) => language.toLowerCase().startsWith('zh')) ||
    navigator.language?.toLowerCase().startsWith('zh')
    ? 'zh'
    : 'en';
}

export function useMeetingI18n() {
  const [locale, setLocale] = React.useState<Locale>('zh');

  React.useEffect(() => {
    const savedLocale =
      typeof window === 'undefined' ? null : window.localStorage.getItem('meeting-locale');
    const detectedLocale =
      savedLocale === 'zh' || savedLocale === 'en' ? savedLocale : detectLocale();
    setLocale(detectedLocale);
    applyDocumentLocale(detectedLocale);
  }, []);

  const switchLocale = React.useCallback(() => {
    setLocale((currentLocale) => {
      const nextLocale = currentLocale === 'zh' ? 'en' : 'zh';
      applyDocumentLocale(nextLocale);
      window.localStorage.setItem('meeting-locale', nextLocale);
      return nextLocale;
    });
  }, []);

  return { ...dictionaries[locale], locale, switchLocale };
}

export function useStoredMeetingI18n() {
  const [locale, setLocale] = React.useState<Locale>('zh');

  React.useEffect(() => {
    const savedLocale = window.localStorage.getItem('meeting-locale');
    const nextLocale = savedLocale === 'zh' || savedLocale === 'en' ? savedLocale : detectLocale();
    setLocale(nextLocale);
    applyDocumentLocale(nextLocale);
  }, []);

  return { ...dictionaries[locale], locale };
}
